'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  /**
   * Ensures the authenticated user always has their own profile row.
   * Called after every sign-in / session restore so a new user whose
   * trigger row was not yet created (e.g. email confirmation race) gets
   * a profile upserted from their auth metadata.
   */
  const ensureUserProfile = async (authUser: any) => {
    if (!authUser) return;
    try {
      // Check if profile already exists
      const { data: existing } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('id', authUser.id)
        .maybeSingle();

      if (!existing) {
        // Profile missing — create it from auth metadata
        const meta = authUser.user_metadata || {};
        const isStudent = meta.is_student === true || meta.is_student === 'true';
        await supabase.from('user_profiles').upsert(
          {
            id: authUser.id,
            email: authUser.email || '',
            full_name: meta.full_name || authUser.email?.split('@')[0] || '',
            avatar_url: meta.avatar_url || '',
            role: meta.role || 'customer',
            phone: meta.phone || '',
            username: meta.username || '',
            // Student traveler fields
            is_student: isStudent,
            student_id: meta.student_id || '',
            school_name: meta.school_name || '',
            school_type: meta.school_type || '',
            year_of_study: meta.year_of_study || '',
            course_program: meta.course_program || '',
            student_verification_status: isStudent ? 'pending' : 'unverified',
            // Guarantor fields (shared with traveler onboarding)
            guarantor_name: meta.guarantor_full_name || '',
            guarantor_phone: meta.guarantor_phone || '',
            guarantor_national_id: meta.guarantor_id_number || '',
            guarantor_relationship: meta.guarantor_relationship || '',
            // Courier fields
            is_courier: meta.role === 'courier',
            courier_approval_status: meta.role === 'courier' ? 'not_applied' : 'not_applied',
          },
          { onConflict: 'id' }
        );
      } else if (existing) {
        // Profile exists — check if student fields need to be backfilled
        const meta = authUser.user_metadata || {};
        const isStudent = meta.is_student === true || meta.is_student === 'true';
        if (isStudent) {
          // Backfill student fields if they were not saved yet
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('is_student, student_id')
            .eq('id', authUser.id)
            .maybeSingle();
          if (profile && !profile.is_student && meta.student_id) {
            await supabase.from('user_profiles').update({
              is_student: true,
              student_id: meta.student_id || '',
              school_name: meta.school_name || '',
              school_type: meta.school_type || '',
              year_of_study: meta.year_of_study || '',
              course_program: meta.course_program || '',
              student_verification_status: 'pending',
              guarantor_name: meta.guarantor_full_name || '',
              guarantor_phone: meta.guarantor_phone || '',
              guarantor_national_id: meta.guarantor_id_number || '',
              guarantor_relationship: meta.guarantor_relationship || '',
            }).eq('id', authUser.id);
          }
        }
      }
    } catch {
      // Non-fatal — profile may already exist or trigger will handle it
    }
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        ensureUserProfile(session.user);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        ensureUserProfile(session.user);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up — passes all metadata so handle_new_user trigger stores role
  const signUp = async (email: string, password: string, metadata: Record<string, any> = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
          role: metadata?.role || 'customer',
          phone: metadata?.phone || '',
          username: metadata?.username || '',
          // Student traveler fields (stored for profile creation)
          is_student: metadata?.isStudent || false,
          student_id: metadata?.studentId || '',
          school_name: metadata?.schoolName || '',
          school_type: metadata?.schoolType || '',
          year_of_study: metadata?.yearOfStudy || '',
          course_program: metadata?.courseProgram || '',
          guarantor_relationship: metadata?.guarantorRelationship || '',
          guarantor_full_name: metadata?.guarantorFullName || '',
          guarantor_phone: metadata?.guarantorPhone || '',
          guarantor_id_number: metadata?.guarantorIdNumber || '',
        },
        emailRedirectTo: `${typeof window !== 'undefined' ? window.location.origin : ''}/auth/callback`
      }
    });
    if (error) throw error;

    // If Supabase auto-confirms (no email verification required), create profile immediately
    if (data.user && data.session) {
      await ensureUserProfile(data.user);
    }

    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;

    // Ensure the signed-in user always has their own profile row
    if (data.user) {
      await ensureUserProfile(data.user);
    }

    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database — always scoped to the current user's id
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    ensureUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
